import { useState, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import { UserContext } from '../../../Contexts/UserContext';
import { screenerAction } from '../../../Config/config';

const ACTION_LABELS = { accept: 'approved', shortlist: 'shortlisted', reject: 'rejected' };

const getNewStatus = (action) => {
  if (action === 'accept') return 'Approved';
  if (action === 'shortlist') return 'Shortlisted';
  return 'Rejected';
};

/**
 * Handles company-level actions: shortlist, approve, reject.
 * Also manages the "pending action" flow when screener isn't saved yet.
 */
const useScreenerActions = ({ selectedScreenerId, updateRows, openSaveModal }) => {
  const [pendingAction, setPendingAction] = useState(null);
  const { getToken, getUser } = useContext(UserContext);

  /** Execute action against API */
  const executeCompanyAction = useCallback(
    async (companyIds, action, screenerId) => {
      try {
        const payload = { user_id: getUser()?.id, action };

        if (action === 'accept') payload.acc_cos_ids = companyIds;
        else if (action === 'shortlist') payload.shrt_cos_ids = companyIds;
        else payload.rej_cos_ids = companyIds;

        if (screenerId) payload.screener_id = screenerId;

        const response = await fetch(screenerAction, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok) {
          updateRows(companyIds, (row) => ({ ...row, status: getNewStatus(action) }));
          toast.success(data.message || `Company ${ACTION_LABELS[action]} successfully!`, {
            position: 'top-center',
          });
        } else {
          toast.error(data?.message || `Failed to ${action} company`, { position: 'top-center' });
        }
      } catch (error) {
        console.error(`Error ${action}ing company:`, error);
        toast.error(`Error ${action}ing company. Please try again.`, { position: 'top-center' });
      }
    },
    [getToken, getUser, updateRows],
  );

  /** Public handler — triggers save modal if screener isn't saved */
  const handleCompanyAction = useCallback(
    async (companyIds, action) => {
      if (!selectedScreenerId) {
        setPendingAction({ companyIds, action });
        openSaveModal();
        return;
      }
      await executeCompanyAction(companyIds, action, selectedScreenerId);
    },
    [selectedScreenerId, executeCompanyAction, openSaveModal],
  );

  const clearPendingAction = useCallback(() => setPendingAction(null), []);

  return {
    pendingAction,
    setPendingAction,
    clearPendingAction,
    handleCompanyAction,
    executeCompanyAction,
  };
};

export default useScreenerActions;
